import type { JSX, PropsWithChildren } from 'react';

import { Radio } from '@change-corgi/design-system-legacy/components/forms';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';
import { Text } from '@change-corgi/design-system-legacy/typography';

import { useSunriseHoldoutExperiment } from '../sunriseHoldoutExperiment/hooks/useSunriseHoldoutExperiment';

import { HighlightText } from './HighlightText';
import { Label } from './Label';

export type Props = PropsWithChildren<{
	highlight?: string;
	label: string;
	message: string;
	onSelect: () => void;
	checked: boolean;
	qaSelectorSelection: string;
	qaSelectorLabel: string;
	qaSelectorMessage: string;
	isSunriseEnabled: boolean;
}>;

// TODO: Improve the complexity of this component once the sunrise experiments are productized
// eslint-disable-next-line complexity,max-lines-per-function
export function Selection({
	qaSelectorSelection,
	qaSelectorLabel,
	qaSelectorMessage,
	label,
	message,
	highlight,
	onSelect,
	checked,
	children,
	...props
}: Props): JSX.Element {
	const { shouldHideRadioButtons, shouldShowSelectionBorder, isSunriseEnabled } = useSunriseHoldoutExperiment({
		checked,
		isSunriseEnabled: props.isSunriseEnabled,
	});

	return (
		<Box
			sx={
				isSunriseEnabled
					? {
							borderColor: shouldShowSelectionBorder ? 'neutral-grey100' : 'transparent',
							borderStyle: 'solid',
							borderWidth: '1px',
							// Note: this is a workaround to use a gradient as a border color.
							background: checked ? 'sunrise.gradient.fill.interactive.default' : 'white',
							borderRadius: 12,
							p: 1,
							boxShadow: checked ? 'elevatedSelected' : 'low',
							cursor: 'pointer',
						}
					: undefined
			}
			onClick={onSelect}
		>
			{highlight && <HighlightText text={highlight} selected={checked} isSunriseEnabled={isSunriseEnabled} />}
			<Box
				variant="bordered"
				sx={{
					p: 10,
					...(highlight
						? { borderRadius: 'standard', borderTopLeftRadius: 0, borderTopRightRadius: 0 }
						: { borderRadius: 'standard' }),
					...(checked ? { borderWidth: 2 } : {}),
					borderColor: checked ? 'secondary-blue' : 'neutral-grey400',
					...(isSunriseEnabled && {
						backgroundColor: 'white',
						borderWidth: 0,
						border: 'none',
						borderRadius: 11, // the parent box has a border radius of 12, so we need to subtract 1px
						// to avoid the border overlap
						px: 24,
						pb: 16,
						pt: highlight ? 12 : 16,
						...(shouldHideRadioButtons && {
							// TODO: Refactor this component to use buttons instead of radio inputs
							// This is a workaround to hide the radio input while keeping the label accessible.
							// eslint-disable-next-line @typescript-eslint/naming-convention
							'input[type="radio"]': {
								position: 'absolute',
								width: 1,
								height: 1,
								padding: 0,
								margin: -1,
								overflow: 'hidden',
								clip: 'rect(0, 0, 0, 0)',
								whiteSpace: 'nowrap',
								border: 0,
							},
						}),
					}),
				}}
			>
				<Radio
					data-qa={qaSelectorSelection}
					data-testid="selection-radio"
					checked={checked}
					readOnly
					label={
						<Box
							sx={{
								cursor: 'pointer',
							}}
						>
							<Label qaSelectorLabel={qaSelectorLabel} label={label} />
							<Flex sx={{ paddingTop: '8px', gap: '8', flexDirection: 'column' }}>
								<Text data-qa={qaSelectorMessage} size="small">
									{message}
								</Text>
								{children}
							</Flex>
						</Box>
					}
					sx={{
						...(isSunriseEnabled && {
							accentColor: 'sunrise.semantic.fill.accent.primary.strong',
						}),
						height: '18px',
						marginTop: '2px',
						width: '18px',
					}}
				/>
			</Box>
		</Box>
	);
}
